import React, { useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useView } from "../context/ViewContext";
import CustomerRoutes from "./CustomerRoutes";
import ProfessionalRoutes from "./ProfessionalRoutes";
import AdminRoutes from "./AdminRoutes";
import Navbar from "../components/Navbar";
import PublicSiteShell from "../components/PublicSiteShell";
import ProfessionalNavbar from "../components/professional/ProfessionalNavbar";
import ProfessionalOnboardingProgressWidget from "../components/professional/ProfessionalOnboardingProgressWidget";
import AdminNavbar from "../components/admin/AdminNavbar";
import { professionalRouteConfig, recruiterRouteConfig } from "./routeConfig";
import { getValidToken } from "../utils/auth";
import {
  canUseProfessionalFeatures,
  getAllowedViewsForAuthPayload,
  getDefaultViewForAuthPayload,
  isPlatformAdminPayload,
} from "../utils/roleCapabilities";
import "./AppRoutes.css";
import { useActiveBusiness } from "../hooks/useActiveBusiness";

const PAGE_TITLES = {
  "/": "Booking and payments",
  "/pricing": "Pricing",
  "/why-haastia": "Why Haastia",
  "/about": "About",
  "/contact": "Contact",
  "/help": "Help center",
  "/example": "Example booking page",
  "/login": "Log in",
  "/signup": "Sign up",
  "/terms": "Terms and conditions",
  "/privacy": "Privacy policy",
  "/pro/overview": "Overview",
  "/bookings": "Bookings",
  "/customers": "Clients",
  "/calendar": "Calendar",
  "/services": "Services",
  "/payments/connect": "Stripe setup",
  "/payments/activity": "Payment activity",
  "/booking-page-visual-editor": "Visual editor",
};

const pageTitleForPath = (path) => {
  const section = PAGE_TITLES[path]
    || (path.startsWith("/bookings/manage/") ? "Manage booking" : null)
    || (path.startsWith("/bookings/") ? "Booking details" : null)
    || (path.startsWith("/customers/") ? "Client details" : null)
    || (path.startsWith("/p/") || path.startsWith("/professional/") ? "Booking page" : null)
    || path.split("/").filter(Boolean).pop()?.replace(/-/g, " ")
    || "Haastia";
  return section === "Haastia" ? section : `${section} | Haastia`;
};

function AppRoutes() {
  const { currentView, setCurrentView } = useView();
  const {
    activeBusinessId,
    business: activeBusiness,
    businesses,
    hydrated: workspaceHydrated,
    pendingBusinessId,
    switching: workspaceSwitching,
    switchError: workspaceSwitchError,
    workspaceVersion,
    refresh: refreshWorkspace,
  } = useActiveBusiness();
  const location = useLocation();
  useEffect(() => {
    document.title = pageTitleForPath(location.pathname);
  }, [location.pathname]);
  // The hydration gate below has no exit of its own: on the visual-editor route it
  // is the entire page, with no navbar to leave by. Surface a way out if the
  // workspace has not resolved in a reasonable time.
  const [hydrationStalled, setHydrationStalled] = useState(false);
  const isPublicProfileRoute = /^\/(?:professional|p)\/[^/]+$/.test(location.pathname);
  // /example is the same booking page rendered from demo data, so it has to sit
  // outside PublicSiteShell for the reason real profiles do: the `.site`
  // stylesheet styles bare h1/h2/p/dl and would bleed into the profile markup.
  // See the header comment in styles/public-site.css.
  const isBookingPageRoute =
    isPublicProfileRoute ||
    Boolean(matchPath({ path: "/example", end: true }, location.pathname));
  // A client's manage-booking link is the business's page, not Haastia's: it
  // draws its own header with the business's name, so whoever opens it gets
  // neither the marketing navbar nor the `.site` styles.
  const isManageBookingRoute = Boolean(
    matchPath({ path: "/bookings/manage/:token", end: true }, location.pathname)
  );
  const isVisualEditorRoute = Boolean(
    matchPath({ path: "/booking-page-visual-editor", end: true }, location.pathname)
  );
  const authLayoutPrefixes = ["/login", "/signin", "/signup", "/phone"];
  const isAuthLayoutRoute = authLayoutPrefixes.some((prefix) =>
    location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
  );
  const isProfessionalDashboardRoute = professionalRouteConfig.some(({ path }) =>
    matchPath({ path, end: true }, location.pathname)
  );
  const isProfessionalOnboardingRoute = Boolean(
    matchPath({ path: "/onboarding", end: true }, location.pathname) ||
      matchPath({ path: "/onboarding/team", end: true }, location.pathname) ||
      matchPath({ path: "/onboarding/team-settings", end: true }, location.pathname) ||
      matchPath({ path: "/onboarding/business-details", end: true }, location.pathname)
  );
  const isRecruiterRoute = recruiterRouteConfig.some(({ path }) =>
    matchPath({ path, end: true }, location.pathname)
  );
  const isProviderDashboardRoute = isProfessionalDashboardRoute || isRecruiterRoute;
  const isProviderShellRoute =
    isProviderDashboardRoute || isBookingPageRoute || isVisualEditorRoute;
  const isAdminRoute = matchPath({ path: "/admin/*" }, location.pathname);

  useEffect(() => {
    if (workspaceHydrated) {
      setHydrationStalled(false);
      return undefined;
    }

    // Slightly longer than the bootstrap request's own timeout, so a request that
    // fails normally gets to render its own error first.
    const timer = setTimeout(() => setHydrationStalled(true), 25000);
    return () => clearTimeout(timer);
  }, [workspaceHydrated]);

  // Detect role from token and adjust context
  useEffect(() => {
    const authData = getValidToken();
    const authPayload = authData?.payload;
    const hasHydratedWorkspaceAccess =
      activeBusiness?.viewerCapabilities?.isWorkspaceMember === true;

    if (!authPayload?.role) {
      setCurrentView("customer");
      return;
    }

    setCurrentView((previousView) => {
      if ((authPayload.businessWorkspaceAccess || hasHydratedWorkspaceAccess) && isProviderDashboardRoute) {
        return isPlatformAdminPayload(authPayload) && isAdminRoute
          ? "admin"
          : "professional";
      }
      if (getAllowedViewsForAuthPayload(authPayload).includes(previousView)) {
        return previousView;
      }

      return getDefaultViewForAuthPayload(authPayload);
    });
  }, [activeBusiness?.viewerCapabilities?.isWorkspaceMember, isAdminRoute, isProviderDashboardRoute, setCurrentView]);

  // Only render one section at a time
  if (!currentView) return <div>Loading...</div>;

  const pendingBusiness = businesses.find((business) => business._id === pendingBusinessId);
  // Single source of truth for the workspace remount key: any consumer of
  // workspace-scoped context that is rendered outside renderWorkspaceRoutes()
  // must carry this same key so it tears down on a switch instead of
  // re-syncing through its own effects.
  const workspaceKey = `${activeBusinessId || "personal"}:${workspaceVersion}`;
  const renderWorkspaceRoutes = () => {
    if (!workspaceHydrated) {
      if (hydrationStalled) {
        return (
          <section className="workspace-transition" role="alert">
            <h1>This is taking longer than expected</h1>
            <p>We couldn&apos;t confirm your workspace. Check your connection and try again.</p>
            <div className="workspace-transition__actions">
              <button type="button" onClick={() => refreshWorkspace?.()}>Try again</button>
              <a href="/pro/overview">Back to dashboard</a>
            </div>
          </section>
        );
      }

      return (
        <section className="workspace-transition" role="status" aria-live="polite">
          <div className="workspace-transition__spinner" aria-hidden="true" />
          <h1>Loading workspace</h1>
          <p>Confirming your access and preparing the latest data…</p>
        </section>
      );
    }
    if (workspaceSwitching) {
      return (
        <section className="workspace-transition" role="status" aria-live="polite">
          <div className="workspace-transition__spinner" aria-hidden="true" />
          <h1>Switching workspace</h1>
          <p>Loading {pendingBusiness?.name || "the selected workspace"}…</p>
        </section>
      );
    }
    return (
      <React.Fragment key={workspaceKey}>
        {workspaceSwitchError && (
          <div className="workspace-switch-error" role="alert">{workspaceSwitchError}</div>
        )}
        <ProfessionalRoutes />
      </React.Fragment>
    );
  };

  if (isManageBookingRoute) {
    return <CustomerRoutes />;
  }

  if (isAuthLayoutRoute) {
    return (
      <PublicSiteShell>
        <Navbar />
        <CustomerRoutes />
      </PublicSiteShell>
    );
  }

  const renderProfessionalShell = () => {
    if (isVisualEditorRoute) {
      return (
        <div className="professional-app-shell professional-app-shell--no-nav professional-app-shell--visual-editor">
          <main className="professional-app-shell__content professional-app-shell__content--visual-editor">
            {renderWorkspaceRoutes()}
          </main>
        </div>
      );
    }

    if (isBookingPageRoute) {
      return (
        <div className="professional-app-shell professional-app-shell--no-nav professional-app-shell--public-profile">
          <main className="professional-app-shell__content professional-app-shell__content--public-profile">
            <ProfessionalRoutes key={workspaceKey} />
          </main>
        </div>
      );
    }

    if (isProviderDashboardRoute) {
      return (
        <div className="professional-app-shell">
          <ProfessionalNavbar key={workspaceKey} />
          <main className="professional-app-shell__content">
            {renderWorkspaceRoutes()}
          </main>
          {!isProfessionalOnboardingRoute && workspaceHydrated &&
            (activeBusiness
              ? activeBusiness.viewerCapabilities?.canAccessProviderProfile
              : canUseProfessionalFeatures(getValidToken()?.payload?.role)) &&
            <ProfessionalOnboardingProgressWidget />}
        </div>
      );
    }

    return (
      <PublicSiteShell>
        <Navbar />
        <ProfessionalRoutes />
      </PublicSiteShell>
    );
  };

  if (currentView === "professional") {
    return renderProfessionalShell();
  }

  if (currentView === "admin" && isPlatformAdminPayload(getValidToken()?.payload)) {
    if (isAdminRoute) {
      return (
        <>
          <AdminNavbar />
          <AdminRoutes />
        </>
      );
    }

    if (isProviderShellRoute) {
      return renderProfessionalShell();
    }

    return (
      <PublicSiteShell>
        <Navbar />
        <CustomerRoutes />
      </PublicSiteShell>
    );
  }

  // Default (customer)
  if (isBookingPageRoute) {
    return <CustomerRoutes />;
  }

  return (
    <PublicSiteShell>
      <Navbar />
      <CustomerRoutes />
    </PublicSiteShell>
  );
}

export default AppRoutes;
